import type {
  OrganizationResponse,
  UpdateCurrentOrganizationCommand,
} from '@acme/api/client';
import { OrganizationService, UserService } from '@acme/api/client';
import { useUser, type UpdateProfileModel } from '@acme/domains';
import { OrganizationGeneralForm, UserGeneralForm } from '@acme/features';
import { AppRoutes } from '@shared/routes';
import { Button, Card } from 'flowbite-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { OrganizationDocuments, TwoFactorAuthentication } from '../ui';

export const ProfileEditPage = () => {
  const navigate = useNavigate();
  const { user } = useUser();

  type OrganizationFormResult = {
    name?: string;
    inn?: string;
    legalAddress?: string;
    actualAddress?: string;
    phoneNumber?: string;
    responsiblePersonName?: string;
    bankDetail?: {
      accountNumber?: string;
      name?: string;
      identificationCode?: string;
      correspondentAccount?: string;
    };
  };

  const [errorsForm, setErrorsForm] = useState<string[]>();
  const [isEdit, setIsEdit] = useState(false);
  const [organization, setOrganization] = useState<OrganizationResponse | null>(
    null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const personalInfoFormRef = useRef<{ submit: () => Promise<void> }>(null);
  const organizationFormRef = useRef<{
    submit: () => Promise<OrganizationFormResult>;
  }>(null);

  const updateProfile: UpdateProfileModel = async (data) => {
    const response = await UserService.putApiV1User({
      requestBody: {
        name: data.name,
        email: data.email,
        phoneNumber: data.phoneNumber,
        inn: data.inn,
        title: data.title,
        passport: {
          seriesNumber: data.passport.seriesNumber,
          dateIssue: data.passport.dateIssue,
          issued: data.passport.issued,
        },
      },
    });

    if (response.errorText) {
      throw new Error(response.errorText);
    }

    return !!response.data;
  };

  useEffect(() => {
    const fetchOrganization = async () => {
      try {
        const response = await OrganizationService.getApiV1Organization();
        if (response.isSuccess && response.data) {
          setOrganization(response.data);
        }
      } catch (error) {
        console.error('Error fetching organization:', error);
      }
    };

    fetchOrganization();
  }, []);

  const handleError = (error: string) => {
    setErrorsForm([error]);
  };

  const handleSave = () => {
    setIsEdit(false);
  };

  const handleCancel = () => {
    setIsEdit(false);
  };

  const getOrganizationData = async () => {
    const orgData =
      (await organizationFormRef.current?.submit()) as OrganizationFormResult | null;
    if (orgData) {
      return orgData;
    }
    return null;
  };

  const handleSubmitForReview = async () => {
    try {
      setIsSubmitting(true);
      setErrorsForm([]);

      await personalInfoFormRef.current?.submit();

      const orgData = await getOrganizationData();
      if (orgData) {
        const updateData: UpdateCurrentOrganizationCommand = {
          name: orgData?.name,
          inn: orgData?.inn,
          legalAddress: orgData?.legalAddress,
          actualAddress: orgData?.actualAddress,
          phoneNumber: orgData?.phoneNumber,
          responsablePerson: orgData?.responsiblePersonName,
          bankDetail: orgData?.bankDetail
            ? {
                bankAccountNumber: orgData.bankDetail.accountNumber,
                nameOfBank: orgData.bankDetail.name,
                bankIdentificationCode: orgData.bankDetail.identificationCode,
                correspondentAccount: orgData.bankDetail.correspondentAccount,
              }
            : undefined,
        };

        await OrganizationService.putApiV1Organization({
          requestBody: updateData,
        });

        const response = await OrganizationService.getApiV1Organization();
        if (response.isSuccess && response.data) {
          setOrganization(response.data);
        }
      }

      setIsEdit(false);
      navigate(AppRoutes.profile.path as string);
    } catch (error) {
      handleError(
        error instanceof Error ? error.message : 'Ошибка при сохранении'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">Общая информация</h2>
          {isEdit ? (
            <div className="flex gap-4">
              <Button
                color="secondary"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                Отмена
              </Button>
              <Button
                color="alternative"
                onClick={handleSave}
                disabled={isSubmitting}
              >
                Сохранить изменения
              </Button>
            </div>
          ) : (
            <></>
            // <Button color="alternative" onClick={() => setIsEdit(true)}>
            //   <HiPencil className="w-4 h-4 mr-2" />
            //   Редактировать
            // </Button>
          )}
        </div>
        {errorsForm && errorsForm.length > 0 && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
            {errorsForm.map((error, index) => (
              <p key={index}>{error}</p>
            ))}
          </div>
        )}
        <UserGeneralForm
          onError={handleError}
          defaultData={user}
          errorsForm={errorsForm}
          includeUnchangedFields={true}
          isEdit={isEdit}
          formRef={personalInfoFormRef}
          updateProfile={updateProfile}
        />
      </Card>

      <OrganizationGeneralForm
        defaultData={organization}
        isEdit={isEdit}
        formRef={organizationFormRef}
      />

      <TwoFactorAuthentication />
      <OrganizationDocuments />
      <Card>
        <div className="flex items-center justify-end">
          <Button
            color="primary"
            onClick={handleSubmitForReview}
            disabled={isSubmitting || isEdit}
          >
            {isSubmitting ? 'Отправка...' : 'Отправить на проверку'}
          </Button>
        </div>
      </Card>
    </div>
  );
};
